import type { NormalizedChart, PalaceId } from './domain.js';
import { buildPalaceEvidenceViewModel } from './precisionPresentation.js';

export interface PalaceNarrativeViewModel {
  model: 'PALACE-NARRATIVE-0.1.0';
  palaceId: PalaceId;
  palaceLabel: string;
  headline: string;
  roleSummary: string;
  structureSummary: string;
  relationSummary: string;
  transformationSummary: string;
  integrationGuide: string;
  safetyNote?: string;
  disclosure: string;
}

const ROLE: Readonly<Record<PalaceId,string>> = Object.freeze({
  life:'命宮是本命閱讀的核心入口，主要用來整理個人反應方式、做事基調與自我定位；成熟解盤仍必須再與遷移、財帛、官祿等三方四正合參。',
  siblings:'兄弟宮在傳統命理中用來觀察手足與近身同輩互動，本產品把它收斂為協調、分工、支援與界線的文化性閱讀。',
  spouse:'夫妻宮用來閱讀伴侶互動與關係中的協作模式；它不是婚姻成功率，也不把單一星曜直接當成伴侶人格。',
  children:'子女宮在傳統語境涉及子女、晚輩與延伸關係；本版只保留結構閱讀，不推論生育能力、子女數量、性別或人生結果。',
  wealth:'財帛宮主要看資源取得、配置、留存與使用節奏；它不直接等於收入高低，更不能單獨作為投資判斷。',
  health:'疾厄宮在傳統命理中涉及身心與生活負荷；本產品只作文化性結構提示與生活節奏提醒，不做疾病、體質或醫療診斷。',
  travel:'遷移宮用來補看人在外部環境、移動、陌生情境與對外互動時呈現的模式，尤其適合與命宮互為表裡地閱讀。',
  friends:'僕役宮是古典名稱，與現代朋友並不完全等義；本產品主要把它用在合作、支援網絡、分工與人際資源的結構閱讀。',
  career:'官祿宮主要看工作角色、責任承接、制度互動與做事節奏；它不直接等於職位、薪資、升遷或社會地位。',
  property:'田宅宮用來閱讀居住、空間、家庭資源配置與長期維護模式；本版不推論房價、持有數量或買賣結果。',
  wellbeing:'福德宮主要看休整、內在節奏、投入方式與精神餘裕的傳統命理脈絡；它不是心理健康或醫療評估。',
  parents:'父母宮在傳統語境涉及父母、長輩與上位支援關係；本版只做互動結構閱讀，不判定親情好壞、壽命或具體事件。',
});
const FOCUS: Readonly<Partial<Record<PalaceId,string>>> = Object.freeze({
  life:'閱讀時先看本宮主星與亮度，再看命遷軸線與財帛、官祿三方，最後才把四化當成放大或牽制條件。',
  siblings:'先看本宮星曜，再看對宮與三方是否提供不同的協調方式；重點是互動模式，而不是替家人貼標籤。',
  spouse:'先看夫妻宮本身，再看對宮與三方；若支持與牽制條件同時存在，應讀成互動中的拉扯，而不是單向吉凶。',
  children:'先保留本宮星曜、三方四正與四化等客觀盤面，再等有來源且通過審核的規則才形成更進一步結論。',
  wealth:'先看財帛宮星曜，再與命宮、官祿及對宮一起理解資源管理方式；四化用來標示資源議題被聚焦的位置。',
  health:'只整理本宮與相關宮位的盤面結構，任何健康語義都停留在傳統文化與生活提醒層。',
});
const FOCUS_MORE: Readonly<Partial<Record<PalaceId,string>>> = Object.freeze({
  travel:'遷移宮要和命宮成對看：命宮偏核心反應，遷移偏外部情境中的表現；三方與四化再補充環境壓力與資源來源。',
  friends:'先看合作與支援網絡的盤面配置，再看三方四正是否讓支援更集中或更分散；不以朋友數量或忠誠度作結論。',
  career:'官祿宮要與命宮、財帛、遷移等宮位合看，才能把本人做事方式、資源條件與外部工作場景分開。',
  property:'田宅宮重點在居住與空間的維持方式，三方四正與四化只說明議題如何被帶動，不直接推出資產結果。',
  wellbeing:'福德宮先看本宮節奏，再看外部宮位如何干擾或支持休整；有煞曜或化忌也只代表需多留意節奏。',
  parents:'先看父母宮本身與相關宮位，再把它理解為長輩／上位互動與支援結構；來源不足時不補造親情結論。',
});

const RELATION_LABEL: Readonly<Record<string,string>> = Object.freeze({ trine:'三方', opposite:'對宮', adjacent:'鄰宮', derived:'延伸', same:'同宮' });
const BRIGHTNESS_LABEL: Readonly<Record<string,string>> = Object.freeze({ temple:'廟', prosperous:'旺', gain:'得', favorable:'利', neutral:'平', weak:'弱', fallen:'陷', none:'—' });

function starLabel(star:any):string {
  const brightness=BRIGHTNESS_LABEL[star.brightness] ?? star.brightness;
  const mutagens=star.birthYearMutagens?.map((item:any)=>item.label).join('、') ?? '';
  return `${star.label}${brightness&&brightness!=='—'?`（${brightness}）`:''}${mutagens?`・${mutagens}`:''}`;
}
function join(items:readonly string[],empty:string):string { return items.length?items.join('、'):empty; }
function transformationLines(evidence:any):readonly string[] {
  const birth=evidence.birthYearTransformations.map((item:any)=>`${item.starLabel}化${item.mutagenLabel}`);
  const incoming=evidence.incomingPalaceStemFlights.map((item:any)=>`${item.sourcePalaceLabel}飛${item.mutagenLabel}入${item.starLabel}`);
  const outgoing=evidence.outgoingPalaceStemFlights.map((item:any)=>`${evidence.palaceLabel}飛${item.mutagenLabel}至${item.targetPalaceLabel}${item.starLabel}`);
  return Object.freeze([...birth,...incoming,...outgoing]);
}

export function buildPalaceNarrative(chart:NormalizedChart,palaceId:PalaceId):PalaceNarrativeViewModel {
  const evidence=buildPalaceEvidenceViewModel(chart,palaceId);
  const stars=evidence.stars.map(starLabel);
  const relations=evidence.relatedPalaces.filter((item)=>item.relation==='trine'||item.relation==='opposite');
  const relationLines=relations.map((item)=>`${RELATION_LABEL[item.relation]??item.relation}${item.palaceLabel}見${join(item.starLabels,'無主要可列星曜')}`);
  const transforms=transformationLines(evidence);
  const structureSummary=`${evidence.palaceLabel}${evidence.isBodyPalace?'同時也是身宮':''}位於${evidence.heavenlyStem}${evidence.earthlyBranch}；本宮星曜為${join(stars,'目前沒有可列星曜')}。這是此宮所有後續判讀的第一層盤面依據。`;
  const relationSummary=relationLines.length?`合看三方四正，可見${relationLines.join('；')}。這些相關宮位用來補足本宮的外部條件與互動背景，不能只抓其中一顆星單獨下結論。`:'目前沒有可列的三方／對宮資料，系統不自行補造關係。';
  const transformationSummary=transforms.length?`四化與宮干飛化層可列出${transforms.join('；')}。這些訊號只表示議題被聚焦、推動或牽制的位置，真正解讀仍要回到本宮星曜與三方四正合參。`:'目前沒有可列的生年四化或宮干飛化條目。';
  const focus=FOCUS[palaceId] ?? FOCUS_MORE[palaceId] ?? `先看${evidence.palaceLabel}本身，再合看三方四正與四化。`;
  const safetyNote=palaceId==='health'
    ? '疾厄宮內容只作傳統文化與生活節奏提醒，不作疾病、體質、心理狀態或治療判斷。'
    : palaceId==='children'
      ? '子女宮不推論生育能力、懷孕結果、子女數量或性別。'
      : palaceId==='parents'
        ? '父母宮不推論父母壽命、疾病或具體家庭事件。'
        : undefined;
  return Object.freeze({
    model:'PALACE-NARRATIVE-0.1.0', palaceId, palaceLabel:evidence.palaceLabel,
    headline:`${evidence.palaceLabel}｜逐宮閱讀`, roleSummary:ROLE[palaceId], structureSummary,
    relationSummary, transformationSummary, integrationGuide:focus,
    ...(safetyNote?{safetyNote}:{}),
    disclosure:'逐宮閱讀把實際盤面整理成可讀脈絡，但不新增未經來源審核的吉凶規則；更具體的性格與人生判讀仍以正式 Rule Engine 為準。',
  });
}

export function buildPalaceNarratives(chart:NormalizedChart):readonly PalaceNarrativeViewModel[] {
  return Object.freeze(chart.palaceOrder.map((palaceId)=>buildPalaceNarrative(chart,palaceId)));
}
